import React, { useState } from 'react';
import { DndContext, closestCorners } from '@dnd-kit/core';
import { Task } from '@/types/tasks';
import { useGoalFilteredTasks } from '@/hooks/tasks/use-goal-filtered-tasks';
import { useGoalTaskBoardDrag } from '../tasks/hooks/task-drag/use-goal-task-board-drag';
import { useTaskSensors } from '../tasks/hooks/useTaskSensors';
import { useDragContext } from '@/contexts/drag/DragContext';
import TaskBoardLoading from '../tasks/components/TaskBoardLoading';
import GoalTaskBoardContent from './GoalTaskBoardContent';

interface GoalTaskBoardProps {
  goalId: string;
  // Function to open page creation modal with task data
  onGenerateContent?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  // Function to open research creation modal with task data
  onGenerateResearch?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  // Function to open image creation modal with task data
  onGenerateImage?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  // Loading state for content generation
  isGeneratingContent?: boolean;
  // Function to open task creation modal
  onCreateTask?: () => void;
  // NEW: Assignee filter props
  selectedAssigneeId?: string;
  onAssigneeChange?: (assigneeId: string) => void;
  teamMembers?: any[];
  isLoadingTeam?: boolean;
  assigneeTaskCounts?: {
    assigneeCounts: Record<string, number>;
    totalTasks: number;
    unassignedCount: number;
  };
  // NEW: Ref to the CreateTaskPanel for scrolling
  createTaskPanelRef?: React.RefObject<HTMLDivElement>;
  workbookId?: string; // For navigation to generated content
}

const GoalTaskBoard: React.FC<GoalTaskBoardProps> = ({ 
  goalId, 
  onGenerateContent, 
  onGenerateResearch,
  onGenerateImage,
  isGeneratingContent, 
  onCreateTask,
  selectedAssigneeId,
  onAssigneeChange,
  teamMembers,
  isLoadingTeam,
  assigneeTaskCounts,
  createTaskPanelRef,
  workbookId
}) => {
  // Lift activeTask state up to be the single source of truth
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [shouldUseLocalTasks, setShouldUseLocalTasks] = useState(false);
  
  // Use goal-filtered task context
  const goalTaskContext = useGoalFilteredTasks(goalId);
  const { 
    activeTasks, 
    completedTasks, 
    isLoading,
    completeTask,
    uncompleteTask,
    removeTask,
    updateTask
  } = goalTaskContext;
  
  // Create task operations object to pass to TaskCards
  const taskOperations = {
    completeTask,
    uncompleteTask,
    removeTask,
    updateTask
  };
  
  // Use drag context to coordinate with page wrapper
  const { setIsDragging } = useDragContext();
  
  // Use specialized goal task board drag functionality
  const { handleDragStart, handleDragOver, handleDragEnd } = useGoalTaskBoardDrag(
    setActiveTask, 
    setShouldUseLocalTasks,
    goalTaskContext
  );
  const sensors = useTaskSensors();

  // Wrap drag handlers to update global drag state
  const wrappedHandleDragStart = (event: any) => {
    setIsDragging(true);
    handleDragStart(event);
  };

  const wrappedHandleDragEnd = (event: any) => {
    setIsDragging(false);
    handleDragEnd(event);
  };

  // Show beautiful skeleton during loading
  if (isLoading) {
    return <TaskBoardLoading activeTaskCount={2} completedTaskCount={1} showCompletedExpanded={false} />;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={wrappedHandleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={wrappedHandleDragEnd}
    >
      <div className="relative" style={{ overflow: 'visible' }}>
        <GoalTaskBoardContent 
          activeTasks={activeTasks}
          completedTasks={completedTasks}
          activeTask={activeTask} 
          setActiveTask={setActiveTask}
          taskOperations={taskOperations}
          onGenerateContent={onGenerateContent}
          onGenerateResearch={onGenerateResearch}
          onGenerateImage={onGenerateImage}
          isGeneratingContent={isGeneratingContent}
          onCreateTask={onCreateTask}
          selectedAssigneeId={selectedAssigneeId}
          onAssigneeChange={onAssigneeChange}
          teamMembers={teamMembers}
          isLoadingTeam={isLoadingTeam}
          assigneeTaskCounts={assigneeTaskCounts}
          createTaskPanelRef={createTaskPanelRef}
          workbookId={workbookId}
        />
      </div>
    </DndContext>
  );
};

export default GoalTaskBoard; 